import { useContext, useMemo } from "react";
import { EUserTypes } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  QUERY_PROJECT_ROLES,
  QUERY_USER_SUBSCRIPTION_ROLES
} from "services/graphQL/ciq-queries";
import { TPermissionComponentLocation, TPermissionRole } from "./types";

type TUsePermissionRolesArgs = {
  "component-location": TPermissionComponentLocation;
};

export const usePermissionRoles = (args: TUsePermissionRolesArgs) => {
  const { gqlClientForProject } = useContext(ProjectContext);
  const componentLocation = args["component-location"];
  const isProjectLevel = componentLocation === "project-level";

  const projectRoleQuery = useCIQQuery<{
    project_role: Array<{ id: number; key: string; name: string }>;
  }>(QUERY_PROJECT_ROLES, {
    variables: {
      where: { type_id: { _eq: EUserTypes.GENERAL_CONTRACTOR } }
    },
    skip: !isProjectLevel || !gqlClientForProject,
    client: gqlClientForProject
  });

  const subscriptionRoleQuery = useCIQQuery<{
    subscription_permission: Array<{ id: number; key: string; name: string }>;
  }>(QUERY_USER_SUBSCRIPTION_ROLES, {
    skip: isProjectLevel
  });

  const roles = useMemo<TPermissionRole[]>(() => {
    if (isProjectLevel) {
      return (
        projectRoleQuery.data?.project_role?.map((role) => ({
          id: role.id,
          key: role.key,
          label: role.name
        })) ?? []
      );
    }

    return (
      subscriptionRoleQuery.data?.subscription_permission?.map((role) => ({
        id: role.id,
        key: role.key,
        label: role.name
      })) ?? []
    );
  }, [
    isProjectLevel,
    projectRoleQuery.data?.project_role,
    subscriptionRoleQuery.data?.subscription_permission
  ]);

  return {
    roles,
    loading: isProjectLevel
      ? projectRoleQuery.loading
      : subscriptionRoleQuery.loading,
    error: isProjectLevel ? projectRoleQuery.error : subscriptionRoleQuery.error
  };
};
